// src/components/vendor/VendorCoupons.tsx
import React, { useState } from 'react';
import {
    Copy,
    Trash2,
    Eye,
    EyeOff,
    Ticket,
    Link as LinkIcon,
    X
} from 'lucide-react';
import './VendorTabs.css';

interface Coupon {
    _id: string;
    code: string;
    discountPercent: number;
    timesUsed: number;
    active: boolean;
    isLink: boolean;
    productId?: {
        _id: string;
        title: string;
        type: string;
    };
    createdAt: string;
}

interface Product {
    _id: string;
    title: string;
    description: string;
    type: string;
    plans: {
        _id: string;
        durationMonths: number | null;
        priceCents: number;
        label: string;
    }[];
}

interface VendorCouponsProps {
    coupons: Coupon[];
    onRefresh: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const VendorCoupons: React.FC<VendorCouponsProps> = ({ coupons, onRefresh, onShowToast }) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createType, setCreateType] = useState<'coupon' | 'link'>('coupon');
    const [couponCode, setCouponCode] = useState('');
    const [couponDiscount, setCouponDiscount] = useState(10);
    const [selectedProduct, setSelectedProduct] = useState<string>('');
    const [products, setProducts] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [creating, setCreating] = useState(false);

    const loadProducts = async () => {
        try {
            setLoadingProducts(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/available-products', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setProducts(data.products || []);
            }
        } catch (error) {
            console.error('Erro ao carregar produtos:', error);
            onShowToast('Erro ao carregar produtos', 'error');
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleOpenModal = (type: 'coupon' | 'link') => {
        setCreateType(type);
        setShowCreateModal(true);
        if (type === 'link') {
            loadProducts();
        }
    };

    const handleCreate = async () => {
        if (!couponCode.trim()) {
            onShowToast('Digite um código', 'error');
            return;
        }

        if (createType === 'link' && !selectedProduct) {
            onShowToast('Selecione um produto', 'error');
            return;
        }

        try {
            setCreating(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/vendor/coupons', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    code: couponCode.trim().toUpperCase(),
                    discountPercent: couponDiscount,
                    productId: createType === 'link' ? selectedProduct : null,
                    isLink: createType === 'link'
                })
            });

            const data = await response.json();

            if (response.ok) {
                onShowToast(`${createType === 'link' ? 'Link' : 'Cupom'} criado com sucesso!`, 'success');
                setShowCreateModal(false);
                setCouponCode('');
                setCouponDiscount(10);
                setSelectedProduct('');
                onRefresh();
            } else {
                onShowToast(data.message || 'Erro ao criar', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao criar', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (couponId: string) => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`/api/vendor/coupons/${couponId}/toggle`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Status atualizado', 'success');
                onRefresh();
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao atualizar', 'error');
        }
    };

    const handleDelete = async (couponId: string) => {
        if (!window.confirm('Tem certeza que deseja deletar?')) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`/api/vendor/coupons/${couponId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Deletado com sucesso', 'success');
                onRefresh();
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao deletar', 'error');
        }
    };

    const copyCouponCode = (code: string) => {
        navigator.clipboard.writeText(code);
        onShowToast('Código copiado!', 'success');
    };

    const copyLink = (code: string, productId: string) => {
        const link = `${window.location.origin}/marketplace/${productId}?coupon=${code}`;
        navigator.clipboard.writeText(link);
        onShowToast('Link copiado!', 'success');
    };

    return (
        <div className="vendor-coupons">
            <div className="vd-coupons-header">
                <div>
                    <h2>Meus Cupons e Links</h2>
                    <p className="vd-coupons-subtitle">
                        Crie cupons genéricos ou links específicos para produtos
                    </p>
                </div>
                <div className="vd-coupons-actions">
                    <button className="vd-btn-create" onClick={() => handleOpenModal('coupon')}>
                        <Ticket size={18} />
                        Criar Cupom
                    </button>
                    <button className="vd-btn-create-link" onClick={() => handleOpenModal('link')}>
                        <LinkIcon size={18} />
                        Criar Link
                    </button>
                </div>
            </div>

            {coupons.length === 0 ? (
                <div className="vd-empty-state">
                    <Ticket size={64} strokeWidth={1.5} />
                    <h3>Nenhum cupom criado</h3>
                    <p>Comece criando seu primeiro cupom ou link de venda</p>
                    <button
                        className="vd-btn-primary"
                        onClick={() => handleOpenModal('coupon')}
                    >
                        Criar Agora
                    </button>
                </div>
            ) : (
                <div className="vd-coupons-grid">
                    {coupons.map(coupon => (
                        <div
                            key={coupon._id}
                            className={`vd-coupon-card ${!coupon.active ? 'vd-inactive' : ''}`}
                        >
                            <div className="vd-coupon-header">
                                <div className="vd-coupon-code-container">
                                    <span className="vd-coupon-code">{coupon.code}</span>
                                    {coupon.isLink ? (
                                        <span className="vd-badge-link">Link</span>
                                    ) : (
                                        <span className="vd-badge-coupon">Cupom</span>
                                    )}
                                </div>
                                <span
                                    className={`vd-coupon-status ${coupon.active ? 'vd-active' : 'vd-inactive'
                                        }`}
                                >
                                    {coupon.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>

                            <div className="vd-coupon-details">
                                <div className="vd-coupon-discount">
                                    {coupon.discountPercent === 0
                                        ? 'Sem desconto'
                                        : `${coupon.discountPercent}% de desconto`}
                                </div>

                                {coupon.productId && (
                                    <div className="vd-coupon-product">
                                        📦 {coupon.productId.title}
                                    </div>
                                )}

                                <div className="vd-coupon-usage">
                                    Usado <strong>{coupon.timesUsed}</strong>{' '}
                                    {coupon.timesUsed === 1 ? 'vez' : 'vezes'}
                                </div>
                            </div>

                            <div className="vd-coupon-actions">
                                {coupon.isLink && coupon.productId ? (
                                    <button
                                        className="vd-btn-copy-link"
                                        onClick={() =>
                                            copyLink(coupon.code, coupon.productId!._id)
                                        }
                                        title="Copiar link"
                                    >
                                        <LinkIcon size={16} />
                                        Copiar Link
                                    </button>
                                ) : (
                                    <button
                                        className="vd-btn-copy-code"
                                        onClick={() => copyCouponCode(coupon.code)}
                                        title="Copiar código"
                                    >
                                        <Copy size={16} />
                                        Copiar
                                    </button>
                                )}

                                <button
                                    className="vd-btn-toggle"
                                    onClick={() => handleToggle(coupon._id)}
                                    title={coupon.active ? 'Desativar' : 'Ativar'}
                                >
                                    {coupon.active ? (
                                        <EyeOff size={16} />
                                    ) : (
                                        <Eye size={16} />
                                    )}
                                </button>

                                <button
                                    className="vd-btn-delete"
                                    onClick={() => handleDelete(coupon._id)}
                                    title="Deletar"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <div
                    className="vd-modal-overlay"
                    onClick={() => !creating && setShowCreateModal(false)}
                >
                    <div
                        className="vd-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vd-modal-header">
                            <h3>
                                {createType === 'link'
                                    ? 'Criar Link de Venda'
                                    : 'Criar Cupom Genérico'}
                            </h3>
                            <button
                                className="vd-btn-close"
                                onClick={() => setShowCreateModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="vd-modal-body">
                            <div className="vd-form-group">
                                <label>Código</label>
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) =>
                                        setCouponCode(e.target.value.toUpperCase())
                                    }
                                    placeholder="EXEMPLO10"
                                    maxLength={20}
                                    disabled={creating}
                                    autoFocus
                                />
                            </div>

                            <div className="vd-form-group">
                                <label>Desconto (%)</label>
                                <input
                                    type="number"
                                    value={couponDiscount}
                                    onChange={(e) =>
                                        setCouponDiscount(
                                            Math.min(
                                                100,
                                                Math.max(0, Number(e.target.value))
                                            )
                                        )
                                    }
                                    min="0"
                                    max="100"
                                    disabled={creating}
                                />
                                <small>
                                    {createType === 'link' && couponDiscount === 0
                                        ? 'Link sem desconto - você recebe 30% de comissão'
                                        : 'O desconto sai da sua comissão'}
                                </small>
                            </div>

                            {createType === 'link' && (
                                <div className="vd-form-group">
                                    <label>Produto</label>
                                    {loadingProducts ? (
                                        <p>Carregando produtos...</p>
                                    ) : (
                                        <select
                                            value={selectedProduct}
                                            onChange={(e) =>
                                                setSelectedProduct(e.target.value)
                                            }
                                            disabled={creating}
                                        >
                                            <option value="">
                                                Selecione um produto
                                            </option>
                                            {products.map(product => (
                                                <option
                                                    key={product._id}
                                                    value={product._id}
                                                >
                                                    {product.title} - R${' '}
                                                    {(
                                                        product.plans[0]?.priceCents /
                                                        100
                                                    ).toFixed(2)}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="vd-modal-actions">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                disabled={creating}
                            >
                                Cancelar
                            </button>
                            <button
                                className="vd-btn-primary"
                                onClick={handleCreate}
                                disabled={creating}
                            >
                                {creating ? 'Criando...' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorCoupons;
